import { UntypedFormGroup } from '@angular/forms';
import { HttpParams } from '@angular/common/http';
import { Sort, SortDirection } from '@angular/material/sort';
import { DataAction } from '@angular-ru/ngxs/decorators';
import { RepositoryActionOptions } from '@angular-ru/ngxs/typings';


function isObjectEqual(obj1: any, obj2: any): boolean {
    if (!obj1 || !obj2) return false;

    const props1 = Object.getOwnPropertyNames(obj1);
    const props2 = Object.getOwnPropertyNames(obj2);

    if (props1.length !== props2.length) return false;

    for (const prop of props1) {
        const bothAreObjects = typeof (obj1[prop]) === 'object' && typeof (obj2[prop]) === 'object';
        if (
            (!bothAreObjects && (obj1[prop] !== obj2[prop])) ||
            (bothAreObjects && !isObjectEqual(obj1[prop], obj2[prop]))
        ) return false;
    }

    return true;
}

function isEntityEqual(entity1: any, entity2: any): boolean {
    return JSON.stringify(entity1) === JSON.stringify(entity2);
}

const matchingPasswords = (targetKey: string, toMatchKey: string) => {
    return (group: UntypedFormGroup): { [p: string]: any } | null => {
        const target = group.controls[targetKey];
        const toMatch = group.controls[toMatchKey];
        const isMatch = target.value === toMatch.value;
        // set equal value error on dirty controls
        if (!isMatch && target.valid && toMatch.valid) {
            toMatch.setErrors({equalValue: true});
            const message = targetKey + ' != ' + toMatchKey;
            return {equalValue: message};
        }
        if (isMatch && toMatch.hasError('equalValue')) {
            toMatch.setErrors(null);
        }

        return null;
    };
};

const createHttpParams = (params: { [key: string]: string | number | boolean | string[] | number[] }): HttpParams => {
    let httpParams: HttpParams = new HttpParams();

    Object.keys(params).forEach(param => {
        const value = params[param];

        if (Array.isArray(value)) {
            value.forEach((item, index) => {
                httpParams = httpParams.append(`${param}[${index}]`, item.toString());
            });
        } else if (value || value === 0) {
            httpParams = httpParams.set(param, value.toString());
        }
    });

    return httpParams;
};


function sortHeader<T>(sortState: Sort, array: Array<T>): Array<T> {
    const data = array.slice();
    if (!sortState.active || sortState.direction === '') {
        return data;
    }
    return data.sort((a: any, b: any) => {
        const isAsc = sortState.direction === 'asc';
        switch (sortState.active) {
            case 'notes':
                return compare(a.notes, b.notes, isAsc);
            case 'storage':
                return compare(a.info, b.info, isAsc);
            case 'id':
                return compare(a.id, b.id, isAsc);
            case 'status':
                return compare(a.status, b.status, isAsc);
            case 'name':
                return compare(a.name, b.name, isAsc);
            case 'location':
                return compare(a.location, b.location, isAsc);
            case 'orientation':
                return compare(a.orientation, b.orientation, isAsc);
            case 'duration':
                return compare(a.duration, b.duration, isAsc);
            case 'media_type':
                return compare(a.mediaType, b.mediaType, isAsc);
            case 'size':
                return compare(a.size, b.size, isAsc);
            case 'url':
                return compare(a.content, b.content, isAsc);
            case 'text':
                return compare(a.content, b.content, isAsc);
            case 'content':
                return compare(a, b, isAsc);
            default:
                return 0;
        }
    });
}

function sortDescAsc(sort: Sort): string {
    const {active, direction} = sort;
    switch (direction) {
        case 'asc':
            return `+${active}`;
            break;
        case 'desc':
            return `-${active}`;
            break;
        default:
            return ``;
    }
}

function setOrderAscDesc(direction: SortDirection): 'ASC' | 'DESC' {
    switch (direction) {
        case 'asc':
            return 'ASC';
            break;
        case 'desc':
            return 'DESC';
        default:
            return 'DESC';
    }
}

function compare(a: any, b: any, isAsc: boolean): any {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
}

function convertToGB(value: number, decimals: number): number {
    return +(value / 1024 / 1024 / 1024).toFixed(decimals);
}

function convertIntoSeconds(minutes: number): number {
    return minutes * 60;
}

function convertIntoMinutes(seconds: number): number {
    return seconds / 60;
}

function removeEmptyPropsFromObj(obj: object): object {
    const copy = JSON.parse(JSON.stringify(obj)) as object;
    for (const prop in copy) {
        if (obj.hasOwnProperty(prop) && copy[prop as keyof object] === undefined || copy[prop as keyof object] === null) {
            // do stuff
            delete copy[prop as keyof object];
        }
    }
    return copy;
}

function normalizeScreenshot(
    screenshot: { date: string; scr: string }
): { date: string; scr: string } {
    if (!screenshot?.scr) {
        return screenshot;
    }

    if (/^data:image\/[a-zA-Z0-9.+-]+;base64,/.test(screenshot.scr)) {
        return screenshot;
    }

    if (screenshot.scr.startsWith('data:image;base64,')) {
        return {
            ...screenshot,
            scr: screenshot.scr.replace(
                'data:image;base64,',
                'data:image/jpeg;base64,'
            ),
        };
    }

    return screenshot;
}

// Search within array with specific object keys. Using in case if search from BE not suitable.
function searchByProps<T extends {[key: string]: any}>(arr: T[], searchValue: string, keys: string[]): T[] {
    return arr.filter((obj) =>
        keys.some((key) => {
            const value = obj[key as keyof T];
            return value && value.toString().toLowerCase().includes(searchValue.toLowerCase());
        }));
}

function DataActionWithoutSubscription(options?: Omit<RepositoryActionOptions, 'subscribeRequired'> | undefined): MethodDecorator {
    return DataAction({subscribeRequired: false, ...options});
}

export {
    isObjectEqual,
    matchingPasswords,
    createHttpParams,
    sortHeader,
    isEntityEqual,
    sortDescAsc,
    convertToGB,
    setOrderAscDesc,
    convertIntoSeconds,
    convertIntoMinutes,
    removeEmptyPropsFromObj,
    searchByProps,
    DataActionWithoutSubscription,
    normalizeScreenshot
};
